import * as React from 'react';
import cn from 'clsx';
import { ArticleHeading2, ArticleHeading3, ArticleParagraph2, UIText1 } from '@cian/ui-kit';

import { withDeviceType } from '../../../../utils/withDeviceType';
import { DealStepModalCard } from '../DealStepModalCard';
import { DealStepModalCardList } from '../DealStepModalCardList';
import { DealStepModalHeadingSection } from '../DealStepModalHeadingSection';
import { NBSP } from '../../../../constants/symbols';
import { IDealStepsModalContentProps } from '../../types';

import * as styles from './styles.css';

const ApplicationStepModalContent: React.FC<React.PropsWithChildren<IDealStepsModalContentProps>> = props => {
  const HeadingComponent = props.isDesktop ? ArticleHeading2 : ArticleHeading3;

  return (
    <>
      <DealStepModalHeadingSection
        imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/house_x1.png"
        imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/house_x2.png"
        chip="В среднем 20 минут"
        title={props.title}
        subtitle="Заявка заполняется онлайн. В любой момент заполнения вы сможете обратиться к профессиональному помощнику."
      />
      <ol className={cn(styles['content'], styles[props.deviceType])}>
        <li className={cn(styles['step'], styles[props.deviceType])}>
          <HeadingComponent as="h5">1. Заполните:</HeadingComponent>
          <DealStepModalCardList>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/building_profile_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/building_profile_x2.png"
              onClick={() => props.onCardClick('objectInfo')}
            >
              Данные об объекте
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/participants_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/participants_x2.png"
              onClick={() => props.onCardClick('participantsInfo')}
            >
              Данные об участниках сделки
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/document_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/document_x2.png"
            >
              Реквизиты счёта продавца
            </DealStepModalCard>
          </DealStepModalCardList>
        </li>

        <li className={cn(styles['step'], styles[props.deviceType])}>
          <HeadingComponent as="h5">2. Загрузите документы:</HeadingComponent>
          <DealStepModalCardList>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/passport_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/passport_x2.png"
            >
              Паспорта
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/snils_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/snils_x2.png"
            >
              СНИЛС
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/document_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/document_x2.png"
            >
              Договор купли-продажи
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/document_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/document_x2.png"
              onClick={() => props.onCardClick('partnerAgreement')}
            >
              Согласие супруга на продажу объекта
            </DealStepModalCard>
          </DealStepModalCardList>
          <div className={cn(styles['documents-step-append'], styles[props.deviceType])}>
            <ArticleParagraph2 color="gray60_100">+ документы для частных случаев</ArticleParagraph2>
          </div>
        </li>

        <li className={cn(styles['step'], styles[props.deviceType])}>
          <HeadingComponent as="h5">3. Выберите способ получения электронных подписей:</HeadingComponent>
          <DealStepModalCardList>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/nfc_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/nfc_x2.png"
              onClick={() => props.onCardClick('foreignPassportScan')}
            >
              Удалённо: через загранпаспорт и смартфон
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/courier_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/courier_x2.png"
              onClick={() => props.onCardClick('courierDelivery')}
            >
              Доставка представителем удостоверяющего центра
            </DealStepModalCard>
          </DealStepModalCardList>
        </li>

        <li className={cn(styles['step'], styles[props.deviceType])}>
          <HeadingComponent as="h5">4. Выберите способ взаиморасчётов:</HeadingComponent>
          <DealStepModalCardList>
            {props.isAccreditiveActive && (
              <DealStepModalCard
                imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/sber_x1.png"
                imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/sber_x2.png"
                onClick={() => props.onCardClick('lc')}
              >
                Аккредитив от СберБанка
              </DealStepModalCard>
            )}
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/cian_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/cian_x2.png"
              onClick={() => props.onCardClick('sbr')}
            >
              Сервис безопасных расчётов Циан{NBSP}&{NBSP}Tinkoff
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/suitcase_x1.png"
              imageX2="https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps_v2/suitcase_x2.png"
              onClick={() => props.onCardClick('other')}
            >
              Самостоятельные расчёты
            </DealStepModalCard>
          </DealStepModalCardList>
          <div className={cn(styles['after-last-step-text'], styles[props.deviceType])}>
            <UIText1>
              После заполнения ссылка на оплату сделки отправится покупателю. Далее помощник проверит все документы и
              свяжется с вами для обсуждения деталей.
            </UIText1>
          </div>
        </li>
      </ol>
    </>
  );
};

const ApplicationStepModalContentWithDeviceType = withDeviceType(ApplicationStepModalContent);
export { ApplicationStepModalContentWithDeviceType as ApplicationStepModalContent };
